import * as React from 'react';

import _ from 'underscore';

import * as Schema from 'nutshell-core/schema';
import type {FilterObject} from 'nutshell-core/types';

import {Checkbox} from 'shells/checkbox/checkbox';
// @ts-expect-error - imported from .js/.jsx file
import {ColumnPickerIcon} from 'shells/icon/svg/column-picker-icon';
import {SAVED_LIST_TOUR_CLOSE_DATE_SECTION_ID} from 'shells/tours/tours';
import {useNutshellTour} from 'shells/tours/use-nutshell-tour';

import styles from './filter-section.module.css';

type Props = {
    field: Schema.FieldProperties;
    filter: FilterObject | null | undefined;
    isOpen: boolean;
    onOpenSection: (filterName: string) => any;
    onCloseSection: (filterName: string) => any;
    onAddFilter?: () => any;
    children: React.ReactNode;
    shownColumns?: string[];
    updateShownColumns?: (arg1: string[], arg2: boolean) => void;
};

export function FilterSection(props: Props) {
    const {shownColumns, updateShownColumns} = props;
    const {progressTour} = useNutshellTour();
    const tourProgressed = React.useRef<boolean>(false);

    const handleAddFilterClicked = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (props.onAddFilter) {
            props.onAddFilter();
        }
    };

    const handleCheckboxClick = () => {
        if (props.isOpen) {
            props.onCloseSection(props.field.name);
        } else {
            props.onOpenSection(props.field.name);
            if (!tourProgressed.current && props.field.name === 'closedTime') {
                progressTour();
                tourProgressed.current = true;
            }
        }
    };

    const renderColumnSelectorButton = () => {
        const isColumnShowing = props.shownColumns
            ? props.shownColumns.includes(props.field.name)
            : undefined;

        return (
            <button
                data-tip={isColumnShowing ? 'Remove column' : 'Add column'}
                onClick={() => onToggleColumn(props.field.name)}
                className={
                    isColumnShowing
                        ? styles['column-button-selected']
                        : styles['column-button-unselected']
                }
            >
                <ColumnPickerIcon size={15} />
            </button>
        );
    };

    const filterHasData = () => {
        if (!props.filter) return false;

        const filterValue = props.filter[props.field.name];

        return (
            filterValue &&
            Array.isArray(filterValue.data) &&
            filterValue.data.length &&
            !_.isEmpty(filterValue.data[0].data) &&
            _.every(filterValue.data, (item) => item.data !== '*')
        );
    };

    const onToggleColumn = (name: string) => {
        if (shownColumns && updateShownColumns) {
            const columnIsShowing = shownColumns.includes(props.field.name);
            let isAddingColumn = false;

            const updatedFields = [...shownColumns];

            if (columnIsShowing) {
                const index = updatedFields.indexOf(name);
                updatedFields.splice(index, 1);
            } else {
                isAddingColumn = true;
                updatedFields.push(name);
            }

            updateShownColumns(updatedFields, isAddingColumn);
        }
    };

    const tourId =
        props.field.name === 'closedTime' ? SAVED_LIST_TOUR_CLOSE_DATE_SECTION_ID : undefined;

    return (
        <div id={tourId} className={props.isOpen ? styles['container-open'] : styles.container}>
            <div className={styles['checkbox-outer-container']}>
                <div className={styles['checkbox-container']}>
                    <Checkbox
                        label={props.field.title}
                        checked={Boolean(props.isOpen || props.filter)}
                        onChange={handleCheckboxClick}
                    />
                </div>
                {props.isOpen &&
                props.field.isDisplayable &&
                props.updateShownColumns &&
                props.shownColumns
                    ? renderColumnSelectorButton()
                    : undefined}
            </div>
            {props.isOpen || props.filter ? props.children : undefined}
            {props.onAddFilter && filterHasData() ? (
                // @ts-expect-error
                <button onClick={handleAddFilterClicked} className={styles['button-add-filter']}>
                    Add filter
                </button>
            ) : undefined}
        </div>
    );
}
